"use client";

import React, { HTMLAttributes, ReactNode, useEffect, useRef, useState } from "react";

import Logo from "@/components/common/Logo";
import RibbonSimple from "@/components/common/Ribbon/RbbonSimple";
import { cn } from "@/utils/general";
import { isComingFromNativeApp } from "@/utils/nativeAppUTMParams";
import { ThemeTypes } from "@/utils/theming";

import styles from "./HeaderReallySimple.module.scss";

interface ContainerProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  className?: string;
}

export const HeaderWrap: React.FC<ContainerProps> = ({ children, className, ...props }) => {
  return (
    <header className={cn(styles.headerWrap, className)} {...props}>
      {children}
    </header>
  );
};

export const HeaderContainer: React.FC<ContainerProps> = ({ children, className, ...props }) => {
  return (
    <div className={cn(styles.headerContainer, className)} {...props}>
      {children}
    </div>
  );
};

export const ContentsContainer: React.FC<ContainerProps> = ({ children, className, ...props }) => {
  return (
    <div className={cn(styles.contentsContainer, className)} {...props}>
      {children}
    </div>
  );
};

interface HeaderReallySimpleProps {
  withRibbon?: boolean;
  theme?: ThemeTypes;
}

const HeaderReallySimple: React.FC<HeaderReallySimpleProps> = ({ withRibbon = false, theme }) => {
  const isMouseInHeader = useRef(false);
  const [yVal, setYVal] = useState(0);
  const [hideHeader, setHideHeader] = useState(false);

  useEffect(() => {
    const { isComingFromApp } = isComingFromNativeApp();
    setHideHeader(isComingFromApp);
  }, []);

  useEffect(() => {
    const bodyElem = document.body;

    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;

      if (!isMouseInHeader.current) {
        if (scrollTop <= 85) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else if (yVal > scrollTop) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else {
          if (!bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.add("pagescrolled");
          }
        }
        setYVal(scrollTop);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [yVal]);

  if (hideHeader) {
    return null;
  }

  return (
    <HeaderWrap
      data-mousedown
      className={cn("header-wrap header-really-simple")}
      onMouseEnter={() => (isMouseInHeader.current = true)}
      onMouseLeave={() => (isMouseInHeader.current = false)}
      onTouchEnd={() =>
        setTimeout(() => {
          isMouseInHeader.current = false;
        })
      }
    >
      <HeaderContainer className={cn(styles[`simpleHeader-${theme}`])}>
        <ContentsContainer className="wrap">
          <div className="flex flex-wrap">
            <div className={cn(styles["left-wrap"])}></div>
            <div className={cn(styles["center-wrap"])}>
              <Logo theme={theme} />
            </div>
            <div className={cn(styles["right-wrap"])}></div>
          </div>
        </ContentsContainer>
      </HeaderContainer>
      {withRibbon && (
        <div style={{ marginTop: "4px" }}>
          <RibbonSimple />
        </div>
      )}
    </HeaderWrap>
  );
};

export default HeaderReallySimple;
